<template>
  <f7-sheet :opened="opened" backdrop class="tx-detail-sheet" @sheet:closed="handleClosed">
    <f7-toolbar>
      <div class="left" />
      <div class="title">{{ $t('wallet.transactions.detail.title') }}</div>
      <div class="right">
        <f7-link sheet-close>{{ $t('general.close') }}</f7-link>
      </div>
    </f7-toolbar>

    <f7-page-content>
      <div v-if="transaction" class="tx-detail">
        <!-- ===== Header: icon · title · date · amount · status ===== -->
        <div class="tx-detail__header">
          <div class="tx-detail__icon" :class="`tx-icon--${iconMeta.tone}`">
            <f7-icon :f7="iconMeta.icon" size="24" aria-hidden="true"></f7-icon>
          </div>
          <div class="tx-detail__headtext">
            <div class="tx-detail__title">{{ title }}</div>
            <div v-if="formattedDate" class="tx-detail__date">{{ formattedDate }}</div>
          </div>
          <f7-chip
            v-if="statusLabel"
            :text="statusLabel"
            :color="statusColor"
            class="tx-detail__status"
          ></f7-chip>
        </div>

        <div class="tx-detail__amount">{{ amountText }}</div>

        <!-- ===== Detail rows ===== -->
        <div class="tx-detail__rows">
          <!-- Fee breakdown — only when the 1% mobile-wallet fee took a cut (L-E):
               what she sent · the wallet's fee · what landed. The row shows only the
               landed figure + the sent hint; the fee itself lives here. -->
          <template v-if="feeBreakdown">
            <div class="tx-detail__row">
              <span class="tx-detail__label">{{ $t('wallet.transactions.detail.feeSent') }}</span>
              <span class="tx-detail__value">{{ formatCurrency(feeBreakdown.grossCents) }}</span>
            </div>
            <div class="tx-detail__row">
              <span class="tx-detail__label">{{ $t('wallet.transactions.detail.feeTaken') }}</span>
              <span class="tx-detail__value">{{ formatCurrency(feeBreakdown.feeCents) }}</span>
            </div>
            <div class="tx-detail__row">
              <span class="tx-detail__label">{{ $t('wallet.transactions.detail.feeLanded') }}</span>
              <span class="tx-detail__value tx-detail__value--strong">{{
                formatCurrency(feeBreakdown.netCents)
              }}</span>
            </div>
          </template>

          <!-- Supplier (standard) -->
          <div v-if="supplierName" class="tx-detail__row">
            <span class="tx-detail__label">{{ $t('wallet.transactions.detail.supplier') }}</span>
            <span class="tx-detail__value">{{ supplierName }}</span>
          </div>

          <!-- Poola offer/item + deal (poola-sourced pool_credit grant/reversal) -->
          <div v-if="poolaOfferTitle" class="tx-detail__row">
            <span class="tx-detail__label">{{ $t('wallet.transactions.detail.poolaOffer') }}</span>
            <span class="tx-detail__value">{{ poolaOfferTitle }}</span>
          </div>
          <div v-if="poolaDealTitle" class="tx-detail__row">
            <span class="tx-detail__label">{{ $t('wallet.transactions.detail.poolaDeal') }}</span>
            <span class="tx-detail__value">{{ poolaDealTitle }}</span>
          </div>

          <!-- Order box name + code — shown for ANY kind the backend could resolve
               a box for, not just invoice rows. A goodwill refund, a write-off
               refund and a PoolShare earning all hang off a box, and naming it is
               the difference between "‏+٧٥ ج.م" and "‏+٧٥ ج.م on the July groceries
               box". The row self-hides when nothing resolved. -->
          <div v-if="orderBoxDisplay" class="tx-detail__row">
            <span class="tx-detail__label">{{ $t('wallet.transactions.detail.orderBox') }}</span>
            <span class="tx-detail__value">{{ orderBoxDisplay }}</span>
          </div>

          <!-- Invoice number — stays invoice-only. A refund references a box, but
               it is not itself an invoice line, so labelling it with an invoice
               number would misstate what the member is looking at. -->
          <div v-if="isInvoice && invoiceDisplayNumber" class="tx-detail__row">
            <span class="tx-detail__label">{{
              $t('wallet.transactions.detail.invoiceNumber')
            }}</span>
            <span class="tx-detail__value tx-detail__value--mono">{{ invoiceDisplayNumber }}</span>
          </div>

          <!-- Notes: refund-reason label, the admin's free-text reason, the member's own note -->
          <div v-if="noteLines.length" class="tx-detail__row tx-detail__row--stack">
            <span class="tx-detail__label">{{ $t('wallet.transactions.detail.notes') }}</span>
            <span class="tx-detail__value">
              <span v-for="(line, i) in noteLines" :key="i" class="tx-detail__note">{{
                line
              }}</span>
            </span>
          </div>

          <!-- Balance before/after — confirmed (posted) rows only -->
          <template v-if="showBalances">
            <div class="tx-detail__row">
              <span class="tx-detail__label">{{
                $t('wallet.transactions.detail.balanceBefore')
              }}</span>
              <span class="tx-detail__value">{{ balanceBeforeText }}</span>
            </div>
            <div class="tx-detail__row">
              <span class="tx-detail__label">{{
                $t('wallet.transactions.detail.balanceAfter')
              }}</span>
              <span class="tx-detail__value tx-detail__value--strong">{{ balanceAfterText }}</span>
            </div>
          </template>
          <div v-else class="tx-detail__hint">
            <f7-icon f7="clock_fill" size="14" aria-hidden="true"></f7-icon>
            <span>{{ $t('wallet.transactions.detail.balancePendingHint') }}</span>
          </div>

          <!-- Lock hint — supplier deposit whose credit isn't cash-withdrawable yet -->
          <div v-if="showReservedLockHint" class="tx-detail__hint tx-detail__hint--lock">
            <f7-icon f7="lock_fill" size="14" aria-hidden="true"></f7-icon>
            <span>{{ $t('wallet.transactions.detail.reservedLockHint') }}</span>
          </div>
        </div>

        <!-- ===== CTAs ===== -->
        <div v-if="canViewInvoice || receiptUrl" class="tx-detail__actions">
          <f7-button
            v-if="canViewInvoice"
            fill
            large
            class="tx-detail__cta"
            @click="handleViewInvoice"
          >
            <f7-icon f7="doc_text_fill" size="18" class="margin-end-half"></f7-icon>
            {{ $t('wallet.transactions.detail.viewInvoice') }}
          </f7-button>
          <f7-button
            v-if="receiptUrl"
            outline
            large
            class="tx-detail__cta"
            @click="handleViewReceipt"
          >
            <f7-icon f7="photo_fill" size="18" class="margin-end-half"></f7-icon>
            {{ $t('wallet.transactions.detail.viewReceipt') }}
          </f7-button>
        </div>
      </div>
    </f7-page-content>
  </f7-sheet>
</template>

<script setup>
import { f7 } from 'framework7-vue';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useCurrency } from '@/composables/useCurrency';
import { useReceiptPhotoBrowser } from '@/composables/wallet/useReceiptPhotoBrowser';
import { TRANSACTION_KIND, TRANSACTION_STATUS } from '@/constants/wallet';
import {
  isInvoiceKind,
  ledgerTitleUsesReason,
  resolveFeeHint,
  resolveLedgerIconMeta,
  resolveLedgerTitle,
  resolveRefundReasonNote,
} from '@/utils/transactionDisplay';

const props = defineProps({
  opened: {
    type: Boolean,
    default: false,
  },
  // The ledger transaction to inspect. Null while the sheet is closed.
  transaction: {
    type: Object,
    default: null,
  },
  // Wallet context the tapped row belongs to (main / poolCredit). Kept for parity with the
  // row component and future wallet-aware rendering.
  walletType: {
    type: String,
    default: 'main',
  },
  // The carton page the sheet is opened from, if any. Its own invoice row must not
  // offer «view invoice» — that would push the page he is already on. A row that
  // belongs to another carton still links to it.
  currentOrderBoxId: {
    type: [String, Number],
    default: null,
  },
});

const emit = defineEmits(['update:opened']);

const { t, te, locale } = useI18n();
const { formatCurrency } = useCurrency();
const { openReceipt } = useReceiptPhotoBrowser();

// Everything reads through a safe view so the closed (null) state never throws.
const tx = computed(() => props.transaction || {});
const kind = computed(() => tx.value.kind);

const isInvoice = computed(() => isInvoiceKind(kind.value));
const iconMeta = computed(() => resolveLedgerIconMeta(kind.value));
const supplierName = computed(() => tx.value.supplierName || null);
const poolaOfferTitle = computed(() => tx.value.poolaOfferTitle || null);
const poolaDealTitle = computed(() => tx.value.poolaDealTitle || null);

const title = computed(() =>
  resolveLedgerTitle({
    kind: kind.value,
    supplierName: supplierName.value,
    poolaOfferTitle: poolaOfferTitle.value,
    poolaDealTitle: poolaDealTitle.value,
    reason: tx.value.reason,
    paymentAccountLabel: tx.value.paymentAccountLabel,
    method: tx.value.meta?.method,
    orderBoxItemName: tx.value.orderBoxItemName,
    orderBoxItemQty: tx.value.orderBoxItemQty,
    t,
    te,
  }),
);

const amountText = computed(() => {
  const cents = tx.value.amountCents ?? 0;
  const sign = cents > 0 ? '+' : '';
  return `${sign}${formatCurrency(cents)}`;
});

// Sent · fee · landed, read off the deposit's safe meta. Null (block hidden) unless a fee
// was actually taken. Landed falls back to the signed amount the ledger posted.
const feeBreakdown = computed(() => {
  const meta = tx.value.meta || {};
  const grossCents = resolveFeeHint({
    grossCents: meta.gross_amount,
    feeCents: meta.commission_amount,
  });
  if (grossCents == null) return null;
  return {
    grossCents,
    feeCents: Number(meta.commission_amount),
    netCents: Number(meta.net_amount ?? tx.value.amountCents ?? 0),
  };
});

const formattedDate = computed(() => {
  if (!tx.value.createdAt) return null;
  // ui-western-digits lock: `-u-nu-latn` forces Western numerals under the ar locale.
  return new Intl.DateTimeFormat(locale.value === 'ar' ? 'ar-EG-u-nu-latn' : 'en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(tx.value.createdAt));
});

const statusLabel = computed(() => {
  const { status } = tx.value;
  if (!status) return null;
  return te(`wallet.transactions.status.${status}`)
    ? t(`wallet.transactions.status.${status}`)
    : null;
});

const statusColor = computed(() => {
  switch (tx.value.status) {
    case TRANSACTION_STATUS.RESERVED:
      return 'orange';
    case TRANSACTION_STATUS.POSTED:
      return 'green';
    default:
      return 'gray';
  }
});

// Balance before/after are BE-populated ONLY for posted (confirmed) transactions — a
// reserved hold hasn't moved the posted balance, so a before/after would mislead. The
// brief pairs the two fields (populated/nulled together); guard on BOTH so a partial null
// can never render a fabricated "0.00" for the missing side.
const showBalances = computed(
  () =>
    tx.value.status === TRANSACTION_STATUS.POSTED &&
    tx.value.balanceBeforeCents != null &&
    tx.value.balanceAfterCents != null,
);
const balanceBeforeText = computed(() => formatCurrency(tx.value.balanceBeforeCents ?? 0));
const balanceAfterText = computed(() => formatCurrency(tx.value.balanceAfterCents ?? 0));

// The precise BE signal: a supplier-direct deposit whose credit is not yet
// cash-withdrawable (order still open). Independent of posted/reserved.
const showReservedLockHint = computed(
  () =>
    kind.value === TRANSACTION_KIND.SUPPLIER_DIRECT_DEPOSIT && tx.value.withdrawalLocked === true,
);

// Order box name + code, e.g. «كرتونة #1 · كود C-1042». Degrades to whichever part exists.
const orderBoxDisplay = computed(() => {
  const boxTitle = tx.value.orderBoxTitle;
  const code = tx.value.orderBoxCode;
  if (boxTitle && code) {
    return t('wallet.transactions.detail.orderBoxValue', { title: boxTitle, code });
  }
  return boxTitle || code || null;
});
const invoiceDisplayNumber = computed(() => tx.value.invoiceDisplayNumber || null);

// Notes, most-specific first:
//   1. the synthesized refund-reason label (always named for refunds — the row
//      title collapses out_of_stock/write_off/goodwill to «مرتجعات»),
//   2. `reason` — the admin's own free-text explanation for THIS transaction,
//      served by the backend for kinds that carry one (goodwill refunds, invoice
//      adjustments). Those kinds now lead the TITLE with it (L-D), so this line only
//      appears when the title could not use it,
//   3. the member's own note off the linked wallet request.
// Any may be absent; the section hides only when all are.
const refundReasonNote = computed(() =>
  resolveRefundReasonNote({ kind: kind.value, supplierName: supplierName.value, t, te }),
);
const noteLines = computed(() => {
  const lines = [];
  if (refundReasonNote.value) lines.push(refundReasonNote.value);
  // A reason-led refund already wears its reason as the title (L-D) — never say it twice.
  if (tx.value.reason && !ledgerTitleUsesReason({ kind: kind.value, reason: tx.value.reason })) {
    lines.push(tx.value.reason);
  }
  if (tx.value.notes) lines.push(tx.value.notes);
  return lines;
});

const linkedOrderBoxId = computed(() => {
  const id = tx.value.meta?.order_box_id;
  if (id == null || id === '' || id === 0) return null;
  return id;
});
const canViewInvoice = computed(() => isInvoice.value
  && linkedOrderBoxId.value != null
  && (props.currentOrderBoxId == null || String(props.currentOrderBoxId) !== String(linkedOrderBoxId.value)));

const receiptUrl = computed(() => tx.value.receiptUrl || null);

function handleClosed() {
  emit('update:opened', false);
}

function handleViewInvoice() {
  const id = linkedOrderBoxId.value;
  if (id == null) return;
  // Close the sheet, then push the invoice totals page onto the main view router
  // (same route the row used to link to directly). `f7.views.main` can be momentarily
  // null during transitions, so optional-chain it like the rest of the app does.
  emit('update:opened', false);
  f7.views.main?.router?.navigate(`/orders/box/${id}/totals/`);
}

function handleViewReceipt() {
  openReceipt(receiptUrl.value);
}
</script>

<style scoped>
.tx-detail-sheet {
  height: 75%;
}

.tx-detail {
  padding: 4px 16px 20px;
}

.tx-detail__header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 0 4px;
}

.tx-detail__icon {
  width: 48px;
  height: 48px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.tx-detail__headtext {
  flex: 1;
  min-width: 0;
}

/* Wraps rather than truncates: a reason-led refund's title is the admin's own sentence,
   and the notes block no longer repeats it, so the sheet is the one place it reads whole. */
.tx-detail__title {
  font-size: 16px;
  font-weight: 600;
  line-height: 1.35;
  color: var(--poolit-text-primary, #1f2937);
  overflow-wrap: anywhere;
}

.tx-detail__date {
  margin-top: 2px;
  font-size: 12px;
  color: var(--poolit-text-muted, #6b7280);
  font-feature-settings:
    'tnum' on,
    'lnum' on;
}

.tx-detail__status {
  flex-shrink: 0;
}

.tx-detail__amount {
  margin: 6px 0 16px;
  font-size: 30px;
  font-weight: 800;
  color: var(--poolit-text-primary, #1f2937);
  font-feature-settings:
    'tnum' on,
    'lnum' on;
}

.tx-detail__rows {
  display: flex;
  flex-direction: column;
  gap: 2px;
  border-radius: 14px;
  background: var(--poolit-surface-subtle, rgba(0, 0, 0, 0.03));
  padding: 4px 14px;
}

.tx-detail__row {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 14px;
  padding: 11px 0;
  border-bottom: 1px solid var(--f7-list-item-border-color, rgba(0, 0, 0, 0.08));
}

.tx-detail__row:last-child {
  border-bottom: none;
}

.tx-detail__row--stack {
  flex-direction: column;
  align-items: stretch;
  gap: 4px;
}

.tx-detail__label {
  font-size: 13px;
  color: var(--poolit-text-muted, #6b7280);
  flex-shrink: 0;
}

.tx-detail__value {
  font-size: 14px;
  font-weight: 500;
  color: var(--poolit-text-primary, #1f2937);
  text-align: end;
  min-width: 0;
  word-break: break-word;
}

.tx-detail__row--stack .tx-detail__value {
  text-align: start;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.tx-detail__value--strong {
  font-weight: 700;
  color: var(--poolit-primary-700, #5b2bb4);
}

.tx-detail__value--mono {
  direction: ltr;
  font-feature-settings:
    'tnum' on,
    'lnum' on;
}

.tx-detail__note {
  line-height: 1.5;
}

.tx-detail__hint {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 11px 0;
  font-size: 12.5px;
  line-height: 1.4;
  color: var(--poolit-text-muted, #6b7280);
}

.tx-detail__hint--lock {
  color: var(--poolit-warning-600, #d97706);
}

.tx-detail__actions {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 18px;
}

.tx-detail__cta {
  width: 100%;
}
</style>
